import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  correctionsService,
  CreateCorrectionInput,
  UpdateCorrectionInput,
} from "../../services/admin/corrections.service";

export function useCorrectionsAdmin() {
  return useQuery({
    queryKey: ["admin", "corrections"],
    queryFn: correctionsService.getAll,
  });
}

export function useCorrection(id?: string) {
  return useQuery({
    queryKey: ["admin", "corrections", id],
    queryFn: () => correctionsService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateCorrection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCorrectionInput) =>
      correctionsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "corrections"] });
      queryClient.invalidateQueries({ queryKey: ["corrections"] });
      notifications.show({
        title: "Success",
        message: "Correction created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create correction",
        color: "red",
      });
    },
  });
}

export function useUpdateCorrection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateCorrectionInput) =>
      correctionsService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "corrections"] });
      queryClient.invalidateQueries({ queryKey: ["corrections"] });
      notifications.show({
        title: "Success",
        message: "Correction updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update correction",
        color: "red",
      });
    },
  });
}

export function useDeleteCorrection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => correctionsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "corrections"] });
      queryClient.invalidateQueries({ queryKey: ["corrections"] });
      notifications.show({
        title: "Success",
        message: "Correction deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete correction",
        color: "red",
      });
    },
  });
}

export function useToggleCorrectionActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      correctionsService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "corrections"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
